import {
  Box,
  Button,
  Card,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  createPushNotificationManagement,
  findPushNotificationManagement,
  patchPushNotificationManagement,
} from '../../store/pushNotificationManagement/api';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import { omit } from 'lodash';
import NotificationFormModal from './NotificationFormModal';
import { IInitialValue } from '../../store/pushNotificationManagement/api/interface';
import notificationSchema from '../../validation/notificationSchema';
import { PushNotificationType } from '../../enum';
import { useEffect, useState } from 'react';
import Scrollbar from '../../components/Scrollbar';
import Loader from '../../components/Loader';
import {
  reset as resetPushNotificationManagement,
  setStatus as setPushNotificationManagementStatus,
} from '../../store/pushNotificationManagement/pushNotificationManagementSlice';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
} from '../../store/pushNotificationManagement/pushNotificationManagementSlice';
import Searchbar from '../../components/Searchbar';
import { formatDate } from '../../utils/formatTime';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { toggle, onOpen, onClose } = useToggle();

  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.pushNotificationManagement);

  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findPushNotificationManagement({
          limit,
          skip,
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetPushNotificationManagement());
  }, [search]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
  } = useFormik<IInitialValue>({
    initialValues: {
      id: '',
      title: '',
      message: '',
      type: '',
      // locationId: '',
      language: '',
    },
    validationSchema: notificationSchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchPushNotificationManagement(values)).unwrap();
        }
        if (!values.id) {
          const reqBody: any = omit(values, ['id']);
          if (reqBody.type === PushNotificationType.corporate) {
            delete reqBody.language;
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
          if (reqBody.type === PushNotificationType.individual) {
            delete reqBody.language;
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
          if (reqBody.type === PushNotificationType.language) {
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
          if (reqBody.type === PushNotificationType.allUsers) {
            delete reqBody.language;
            await dispatch(createPushNotificationManagement(reqBody)).unwrap();
          }
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setPushNotificationManagementStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <>
      <Page title={'Notification: List'}>
        <Container maxWidth={'xl'}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            spacing={{ xs: 0.5, sm: 1.5 }}
            my={1}
          >
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={onOpen}
            >
              {'New Notification'}
            </Button>
          </Stack>

          <Card>
            <Searchbar handleSearch={handleSearch} />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Title'}</TableCell>
                      <TableCell align='center'>{'Message'}</TableCell>
                      <TableCell align='center'>{'Type'}</TableCell>
                      <TableCell align='center'>{'Date'}</TableCell>
                      <TableCell align='center'>{'Details'}</TableCell>
                      <TableCell align='center'>{'Sender Name'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((pushNotificationManagement) => (
                        <TableRow key={pushNotificationManagement.id}>
                          <TableCell align='center'>
                            {pushNotificationManagement?.title}
                          </TableCell>
                          <TableCell align='center'>
                            {pushNotificationManagement?.message}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{ textTransform: 'capitalize' }}
                          >
                            {pushNotificationManagement?.type}
                          </TableCell>
                          <TableCell align='center'>
                            {formatDate(pushNotificationManagement?.createdAt)}
                          </TableCell>

                          {/* {pushNotificationManagement?.type ===
                            PushNotificationType.allUsers && (
                            <TableCell align='center'>-</TableCell>
                          )}
                          {pushNotificationManagement?.type ===
                            PushNotificationType.location && (
                            <TableCell align='center'>
                              {
                                pushNotificationManagement?.location_management
                                  ?.labelEn
                              }
                            </TableCell>
                          )} */}

                          {pushNotificationManagement?.type ===
                          PushNotificationType.language ? (
                            <TableCell align='center'>
                              {pushNotificationManagement?.language}
                            </TableCell>
                          ) : (
                            <TableCell align='center'>-</TableCell>
                          )}

                          <TableCell align='center'>
                            {
                              pushNotificationManagement?.senderUserDetails
                                ?.name
                            }
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>

          <NotificationFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>
      </Page>
    </>
  );
};

export default Index;
